import React, { useState } from "react";
import { toast } from "react-toastify";
import { Search, X, ChevronLeft, ChevronRight } from "lucide-react";

import {
    SearchPamCodesByLocationAsync,
    PamLocationData,
} from "../../Admin-Api/SearchPamCodesByLocationAsync";

type SearchType = "state" | "district" | "postOffice";

interface PamCodeSearchProps {
    pageSize?: number;
    showTitle?: boolean;
}

const PamCodeSearch: React.FC<PamCodeSearchProps> = ({
    pageSize = 10,
    showTitle = true,
}) => {
    const [searchType, setSearchType] = useState<SearchType>("state");
    const [searchValue, setSearchValue] = useState("");

    const [pamLocations, setPamLocations] = useState<PamLocationData[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const fetchPamLocations = async (
        page: number,
        type: SearchType,
        value: string
    ) => {
        const trimmedValue = value.trim();

        if (!trimmedValue) {
            const lookupLabel =
                type === "state"
                    ? "state"
                    : type === "district"
                        ? "district"
                        : "post office";

            toast.info(`Please enter a ${lookupLabel} name.`);
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem("token");

            if (!token) {
                toast.error("Authentication token not found.");
                return;
            }

            const stateName =
                type === "state" ? trimmedValue : "";

            const districtName =
                type === "district" ? trimmedValue : "";

            const postOfficeName =
                type === "postOffice" ? trimmedValue : "";

            const response = await SearchPamCodesByLocationAsync(
                stateName,
                districtName,
                postOfficeName,
                page,
                pageSize,
                token
            );

            setPamLocations(response.data || []);
            setTotalRecords(response.totalRecords || 0);
            setPageNumber(response.pageNumber || page);
        } catch (error) {
            console.error(
                "Error searching PAM codes:",
                error
            );

            toast.error("Failed to search PAM codes.");

            setPamLocations([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        if (!searchValue.trim()) {
            const lookupLabel =
                searchType === "state"
                    ? "state"
                    : searchType === "district"
                        ? "district"
                        : "post office";

            toast.info(`Please enter a ${lookupLabel} name.`);
            return;
        }

        setHasSearched(true);

        fetchPamLocations(
            1,
            searchType,
            searchValue
        );
    };

    const handleClear = () => {
        setSearchValue("");
        setPamLocations([]);
        setTotalRecords(0);
        setPageNumber(1);
        setHasSearched(false);
    };

    const handleSearchTypeChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setSearchType(e.target.value as SearchType);
        setSearchValue("");

        setPamLocations([]);
        setTotalRecords(0);
        setPageNumber(1);
        setHasSearched(false);
    };

    const handlePrevious = () => {
        if (pageNumber <= 1 || loading) return;

        fetchPamLocations(
            pageNumber - 1,
            searchType,
            searchValue
        );
    };

    const handleNext = () => {
        const totalPages = Math.ceil(
            totalRecords / pageSize
        );

        if (pageNumber >= totalPages || loading) return;

        fetchPamLocations(
            pageNumber + 1,
            searchType,
            searchValue
        );
    };

    const totalPages = Math.ceil(
        totalRecords / pageSize
    );

    const getVisiblePages = (): (number | "left-dots" | "right-dots")[] => {
        const pages: (number | "left-dots" | "right-dots")[] = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        pages.push(1);

        if (pageNumber > 4) {
            pages.push("left-dots");
        }

        const startPage = Math.max(2, pageNumber - 1);
        const endPage = Math.min(totalPages - 1, pageNumber + 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (pageNumber < totalPages - 3) {
            pages.push("right-dots");
        }

        pages.push(totalPages);

        return pages;
    };

    return (
        <div className="w-full px-3 py-4 sm:px-5 sm:py-5 lg:px-8">
            <div className="w-full max-w-full">
                {/* Title */}
                {showTitle && (
                    <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-800">
                            Search PAM Codes
                        </h4>

                        <p className="text-sm text-gray-500 mt-1">
                            Search PAM codes by state, district, or post office name
                        </p>
                    </div>
                )}

                {/* Search Card */}
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">

                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end">

                        {/* Search Type */}
                        <div className="w-full lg:w-48 lg:shrink-0">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Search by
                            </label>

                            <select
                                value={searchType}
                                onChange={handleSearchTypeChange}
                                className="w-full h-10 px-3 border border-gray-300 rounded-md
                                bg-white text-sm text-gray-700
                                focus:outline-none focus:ring-2
                                focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="state">
                                    State
                                </option>

                                <option value="district">
                                    District
                                </option>

                                <option value="postOffice">
                                    Post Office
                                </option>
                            </select>
                        </div>

                        {/* Search Input */}
                        <div className="flex-1 min-w-0">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {searchType === "state"
                                    ? "State Name"
                                    : searchType === "district"
                                        ? "District Name"
                                        : "Post Office Name"}
                            </label>

                            <div className="relative">
                                <Search
                                    size={18}
                                    className="absolute left-3 top-1/2
                                    -translate-y-1/2 text-gray-400"
                                />

                                <input
                                    type="text"
                                    value={searchValue}
                                    onChange={(e) =>
                                        setSearchValue(
                                            e.target.value
                                        )
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSearch();
                                        }
                                    }}
                                    placeholder={
                                        searchType === "state"
                                            ? "Enter state name"
                                            : searchType === "district"
                                                ? "Enter district name"
                                                : "Enter post office name"
                                    }
                                    className="w-full h-10 pl-10 pr-3
                                    border border-gray-300 rounded-md
                                    text-sm text-gray-700
                                    focus:outline-none focus:ring-2
                                    focus:ring-indigo-500
                                    focus:border-indigo-500"
                                />
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto lg:shrink-0">

                            <button
                                type="button"
                                onClick={handleSearch}
                                disabled={loading}
                                className="h-10 w-full px-5 flex items-center justify-center gap-2
        bg-indigo-600 text-white rounded-md text-sm font-medium
        hover:bg-indigo-700
        disabled:opacity-50
        disabled:cursor-not-allowed
        sm:w-auto"
                            >
                                <Search size={16} />
                                {loading ? "Searching..." : "Search"}
                            </button>

                            <button
                                type="button"
                                onClick={handleClear}
                                disabled={loading && pamLocations.length === 0}
                                className="h-10 w-full px-5 flex items-center justify-center gap-2
        bg-gray-100 text-gray-700 rounded-md text-sm font-medium
        hover:bg-gray-200
        disabled:opacity-50
        sm:w-auto"
                            >
                                <X size={16} />
                                Clear
                            </button>

                        </div>
                    </div>
                </div>

                {/* Loading */}
                {/* Loading */}
                {loading && (
                    <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-600" />
                            <p className="mt-3 text-sm font-medium text-gray-500">
                                Loading PAM codes...
                            </p>
                        </div>
                    </div>
                )}

                {/* Results */}
                {!loading && pamLocations.length > 0 && (
                    <div className="mt-6">

                        {/* Results Header */}
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

                            <div>
                                <div className="flex items-center gap-2">
                                    <h5 className="text-base font-semibold text-gray-900">
                                        PAM Code Results
                                    </h5>
                                </div>

                            </div>

                            {totalPages > 0 && (
                                <div className="text-sm text-gray-500">
                                    Page{" "}
                                    <span className="font-medium text-gray-700">
                                        {pageNumber}
                                    </span>{" "}
                                    of{" "}
                                    <span className="font-medium text-gray-700">
                                        {totalPages}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Result Card */}
                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">

                            {/* Location Header */}
                            <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50/70 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                        Location
                                    </p>

                                    <div className="mt-1 flex flex-wrap items-center gap-2">
                                        {[
                                            pamLocations[0].stateName,
                                            pamLocations[0].districtName,
                                            pamLocations[0].postOfficeName,
                                        ]
                                            .filter(Boolean)
                                            .map((locationPart, index, filteredParts) => (
                                                <React.Fragment key={`${locationPart}-${index}`}>
                                                    <span
                                                        className={index === 0 ? "text-sm font-semibold text-gray-900" : "text-sm text-gray-600"}
                                                    >
                                                        {locationPart}
                                                    </span>

                                                    {index < filteredParts.length - 1 && (
                                                        <span className="text-gray-300">/</span>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                    </div>
                                </div>

                                <div className="text-xs text-gray-400">
                                    {totalRecords.toLocaleString()} records
                                </div>
                            </div>

                            {/* Table */}
                            <div className="w-full overflow-x-auto">
                                <table className="min-w-[650px] w-full">

                                    <thead>
                                        <tr className="border-b border-gray-200 bg-white">
                                            <th className="whitespace-nowrap px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                Pincode
                                            </th>

                                            <th className="whitespace-nowrap px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                Block Code
                                            </th>

                                            <th className="whitespace-nowrap px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                Address Code
                                            </th>

                                            <th className="whitespace-nowrap px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                                PAM Code
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-100">
                                        {pamLocations.map((item, index) => (
                                            <tr
                                                key={`${item.pamCode}-${index}`}
                                                className="group transition-colors hover:bg-gray-50/70"
                                            >
                                                <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-700">
                                                    {item.pincode || "-"}
                                                </td>

                                                <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-700">
                                                    {item.blockCode || "-"}
                                                </td>

                                                <td className="whitespace-nowrap px-5 py-3.5 text-sm  text-gray-700">
                                                    {item.addressCode || "-"}
                                                </td>

                                                <td className="whitespace-nowrap px-5 py-3.5 text-sm font-medium text-blue-700">
                                                    {`${item.pincode}-${item.blockCode}-${item.addressCode}`}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>

                                </table>
                            </div>

                            

                        </div>
                    </div>
                )}

                {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex w-full justify-center px-2 mt-5 sm:mt-8">
                                    <div className="flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg border border-gray-200 bg-white px-1 py-1 shadow-sm sm:gap-1 sm:px-2">

                                        {/* Previous */}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                fetchPamLocations(
                                                    pageNumber - 1,
                                                    searchType,
                                                    searchValue
                                                )
                                            }
                                            disabled={pageNumber === 1 || loading}
                                            className="flex h-9 min-w-[36px] shrink-0 items-center justify-center
                    rounded-md px-2 text-gray-500
                    hover:bg-gray-100
                    disabled:cursor-not-allowed
                    disabled:opacity-40
                    sm:h-10 sm:min-w-[40px] sm:px-3"
                                        >
                                            «
                                        </button>

                                        {/* Page Numbers */}
                                        {getVisiblePages().map((item, index) =>
                                            item === "left-dots" || item === "right-dots" ? (
                                                <span
                                                    key={`${item}-${index}`}
                                                    className="shrink-0 px-1.5 text-xs text-gray-400 sm:px-2 sm:text-sm"
                                                >
                                                    ...
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    key={item}
                                                    onClick={() =>
                                                        fetchPamLocations(
                                                            item,
                                                            searchType,
                                                            searchValue
                                                        )
                                                    }
                                                    disabled={loading}
                                                    className={`flex h-9 min-w-[36px] shrink-0 items-center justify-center
                            rounded-lg text-xs font-medium transition
                            sm:h-10 sm:min-w-[40px] sm:text-sm
                            ${pageNumber === item
                                                            ? "bg-blue-600 text-white shadow-sm"
                                                            : "text-gray-700 hover:bg-gray-100"
                                                        }`}
                                                >
                                                    {item}
                                                </button>
                                            )
                                        )}

                                        {/* Next */}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                fetchPamLocations(
                                                    pageNumber + 1,
                                                    searchType,
                                                    searchValue
                                                )
                                            }
                                            disabled={pageNumber === totalPages || loading}
                                            className="flex h-9 min-w-[36px] shrink-0 items-center justify-center
                    rounded-md px-2 text-gray-500
                    hover:bg-gray-100
                    disabled:cursor-not-allowed
                    disabled:opacity-40
                    sm:h-10 sm:min-w-[40px] sm:px-3"
                                        >
                                            »
                                        </button>

                                    </div>
                                </div>
                            )}

                {/* No Results */}
                {hasSearched &&
                    !loading &&
                    totalRecords === 0 &&
                    pamLocations.length === 0 && (
                        <div className="mt-6 rounded-xl border border-gray-200 bg-white px-6 py-10 text-center shadow-sm">

                            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                                <Search size={18} className="text-gray-400" />
                            </div>

                            <p className="mt-3 text-sm font-medium text-gray-700">
                                No PAM codes found
                            </p>

                            <p className="mt-1 text-sm text-gray-500">
                                We couldn't find any PAM codes for{" "}
                                <span className="font-medium text-gray-700">
                                    "{searchValue.trim()}"
                                </span>
                            </p>

                            <p className="mt-2 text-xs text-gray-400">
                                Try searching with a different state, district, or post office name.
                            </p>

                        </div>
                    )}
            </div>
        </div>
    );
};

export default PamCodeSearch;